import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  Heart,
  Share2,
  Star,
  Minus,
  Plus,
  Truck,
  Shield,
  RotateCcw,
  ChevronRight,
  Package,
  Wrench,
  ArrowLeft,
} from "lucide-react";
import productDatabase from "../data/productDatabase";
import { useDispatch } from "react-redux";
import { addToCart } from "../redux/cartSlice";

// Animated Section Component
function SectionCard({ title, icon: Icon, isOpen, onClick, children, count, color }) {
  return (
    <motion.div
      layout
      className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 ${
        isOpen ? "shadow-lg" : "hover:shadow-md"
      }`}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-5 bg-white"
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-500">{count} items available</p>
          </div>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
        >
          <ChevronDown className="w-5 h-5 text-gray-600" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-gray-100"
          >
            <div className="p-5 bg-gray-50/50">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Related Product Card
function RelatedProductCard({ product, onAddToCart }) {
  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
    >
      <div className="relative h-40 overflow-hidden">
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 px-2 py-1 bg-white/90 backdrop-blur rounded-lg text-xs font-medium text-gray-700">
          {product.brand}
        </div>
      </div>
      <div className="p-4">
        <h4 className="font-semibold text-gray-800 mb-1 line-clamp-1">{product.title}</h4>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <span className="text-xs text-gray-500">(4.8)</span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-lg font-bold text-gray-800">
            ₹{product.parts[0]?.price || 0}
          </p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => onAddToCart(product)}
            className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-medium shadow-lg shadow-blue-500/30"
          >
            Add
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

// Part Price Item
function PartPriceItem({ part, index }) {
  const [quantity, setQuantity] = useState(1);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-300 transition-all group"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
          <Package className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h4 className="font-semibold text-gray-800">{part.name}</h4>
          <p className="text-sm text-gray-500">In Stock</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <Minus size={16} />
          </button>
          <span className="w-10 text-center font-medium">{quantity}</span>
          <button
            onClick={() => setQuantity(quantity + 1)}
            className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <Plus size={16} />
          </button>
        </div>
        <p className="text-xl font-bold text-gray-800 min-w-[80px] text-right">
          ₹{part.price * quantity}
        </p>
      </div>
    </motion.div>
  );
}

// Breadcrumb Component
function Breadcrumb({ items }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
      <button onClick={() => window.history.back()} className="flex items-center gap-1 hover:text-gray-700">
        <ArrowLeft size={16} />
        Back
      </button>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <ChevronRight size={14} />
          <span className={index === items.length - 1 ? "text-gray-800 font-medium" : "hover:text-gray-700"}>
            {item}
          </span>
        </React.Fragment>
      ))}
    </nav>
  );
}

export default function ProductDetails() {
  const { id } = useParams();
  const data = productDatabase[id];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("specs");

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Package className="w-12 h-12 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Product Not Found</h1>
          <p className="text-gray-500 mb-6">The product you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30"
          >
            Go Back
          </button>
        </motion.div>
      </div>
    );
  }

  const toggleSection = (key) => {
    setOpenSection(openSection === key ? null : key);
  };

  const handleAddToCart = (product) => {
    dispatch(
      addToCart({
        id: product.id,
        title: product.title,
        price: product.parts[0]?.price || 0,
        image: product.image,
      })
    );
  };

  const getRelatedProducts = (key, items) => {
    return Object.values(productDatabase).filter(
      (p) => p[key]?.some((r) => items.includes(r))
    ).slice(0, 4);
  };

  const allRelatedItems = [
    ...new Set([
      ...(data.rawMaterials || []),
      ...(data.newParts || []),
      ...(data.oldParts || []),
    ]),
  ];

  const relatedProducts = getRelatedProducts("rawMaterials", allRelatedItems);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <Breadcrumb items={[data.brand, data.title]} />

        {/* Main Product Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-lg overflow-hidden mb-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Image Gallery */}
            <div className="space-y-4">
              <motion.div
                key={selectedImage}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative rounded-2xl overflow-hidden bg-gray-100 aspect-video"
              >
                <img
                  src={data.image}
                  alt={data.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    In Stock
                  </span>
                </div>
                <div className="absolute top-4 right-4 flex gap-2">
                  <button className="w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors">
                    <Heart className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors">
                    <Share2 className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </motion.div>
              
              {/* Thumbnail Gallery */}
              <div className="flex gap-3 overflow-x-auto pb-2">
                {[data.image, data.image, data.image].map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index ? "border-blue-500 shadow-lg" : "border-transparent opacity-70"
                    }`}
                  >
                    <img src={img} alt={`Thumbnail ${index}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-sm font-medium">
                    {data.brand}
                  </span>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                    ))}
                    <span className="text-sm text-gray-500 ml-1">(48 reviews)</span>
                  </div>
                </div>
                <h1 className="text-4xl font-bold text-gray-800 mb-3">{data.title}</h1>
                <p className="text-gray-600 text-lg leading-relaxed">{data.description}</p>
              </div>

              {/* Price Card */}
              <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
                <div className="flex items-end gap-3 mb-4">
                  <span className="text-4xl font-bold text-gray-800">
                    ₹{data.parts[0]?.price || 0}
                  </span>
                  <span className="text-lg text-gray-500 line-through">₹{(data.parts[0]?.price || 0) * 1.2}</span>
                  <span className="px-2 py-1 bg-red-100 text-red-600 rounded-lg text-sm font-medium">17% OFF</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Quantity:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                      >
                        <Minus size={18} />
                      </button>
                      <span className="w-12 text-center font-semibold text-lg">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAddToCart(data)}
                  className="flex-1 flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-semibold text-lg shadow-lg shadow-blue-500/30 hover:shadow-xl transition-shadow"
                >
                  <ShoppingCart size={22} />
                  Add to Cart
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl font-semibold text-lg shadow-lg shadow-green-500/30 hover:shadow-xl transition-shadow"
                >
                  Buy Now
                </motion.button>
              </div>

              {/* Benefits */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-2xl">
                <div className="text-center">
                  <Truck className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="text-xs text-gray-600">Free Delivery</p>
                </div>
                <div className="text-center">
                  <Shield className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="text-xs text-gray-600">2 Year Warranty</p>
                </div>
                <div className="text-center">
                  <RotateCcw className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <p className="text-xs text-gray-600">Easy Returns</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: "specs", label: "Specifications" },
            { key: "parts", label: "Parts & Pricing" },
            { key: "related", label: "Related Products" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeTab === tab.key
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30"
                  : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "specs" && (
            <motion.div
              key="specs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <SectionCard
                title="Brand Information"
                icon={Star}
                count={1}
                color="from-blue-500 to-cyan-500"
                isOpen={openSection === "brand"}
                onClick={() => toggleSection("brand")}
              >
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-500">Brand Name</span>
                    <span className="font-medium text-gray-800">{data.brand}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-500">Country of Origin</span>
                    <span className="font-medium text-gray-800">India</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Warranty</span>
                    <span className="font-medium text-gray-800">2 Years</span>
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title="Available Models"
                icon={Wrench}
                count={data.models.length}
                color="from-purple-500 to-pink-500"
                isOpen={openSection === "models"}
                onClick={() => toggleSection("models")}
              >
                <div className="grid grid-cols-2 gap-3">
                  {data.models.map((model, index) => (
                    <div key={index} className="p-3 bg-white rounded-lg border border-gray-200">
                      <p className="font-medium text-gray-800">{model}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard
                title="Raw Materials"
                icon={Package}
                count={data.rawMaterials.length}
                color="from-green-500 to-emerald-500"
                isOpen={openSection === "raw"}
                onClick={() => toggleSection("raw")}
              >
                <div className="space-y-2">
                  {data.rawMaterials.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-medium text-gray-700">{item}</span>
                      <button className="text-sm text-blue-600 hover:text-blue-700">View Details</button>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard
                title="New Parts"
                icon={Plus}
                count={data.newParts.length}
                color="from-orange-500 to-red-500"
                isOpen={openSection === "newParts"}
                onClick={() => toggleSection("newParts")}
              >
                <div className="space-y-2">
                  {data.newParts.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-medium text-gray-700">{item}</span>
                      <button className="text-sm text-blue-600 hover:text-blue-700">View Details</button>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard
                title="Used Parts"
                icon={RotateCcw}
                count={data.oldParts.length}
                color="from-gray-500 to-gray-700"
                isOpen={openSection === "oldParts"}
                onClick={() => toggleSection("oldParts")}
              >
                <div className="space-y-2">
                  {data.oldParts.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-medium text-gray-700">{item}</span>
                      <button className="text-sm text-blue-600 hover:text-blue-700">View Details</button>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </motion.div>
          )}

          {activeTab === "parts" && (
            <motion.div
              key="parts"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-3xl shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Parts & Pricing</h2>
              <div className="space-y-4">
                {data.parts.map((part, index) => (
                  <PartPriceItem key={index} part={part} index={index} />
                ))}
              </div>
              
              {/* Total */}
              <div className="mt-8 p-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white/80">Total Parts Cost</p>
                    <p className="text-3xl font-bold">
                      ₹{data.parts.reduce((sum, part) => sum + part.price, 0)}
                    </p>
                  </div>
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAddToCart(data)}
                    className="px-8 py-3 bg-white text-blue-600 rounded-xl font-semibold shadow-lg"
                  >
                    Add All to Cart
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "related" && (
            <motion.div
              key="related"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Related Products</h2>
              {relatedProducts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {relatedProducts.map((product) => (
                    <RelatedProductCard
                      key={product.id}
                      product={product}
                      onAddToCart={handleAddToCart}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-3xl">
                  <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No related products found</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
